import type { DestinationUrlInput } from 'features/campaign/campaign.types';
import { useCallback, useEffect, useState } from 'react';
import { parseUrl, validateLinkValue } from '../utils';

type UseUrlLogicProps = {
  value: string;
  platform?: string;
};

type UseUrlLogicReturn = {
  linkType: DestinationUrlInput;
  linkValue: string;
  showVariations: boolean;
  validationError: string | undefined;
  handleLinkTypeChange: (newType: DestinationUrlInput) => void;
  handleLinkValueChange: (newValue: string) => void;
  handleShowVariationsChange: (checked: boolean) => void;
};

/**
 * Custom hook that manages URL-related state and logic for LinkBuilder widgets
 */
export const useUrlLogic = ({ value, platform }: UseUrlLogicProps): UseUrlLogicReturn => {
  const parsedLinkComponents = parseUrl(value, platform);

  const [linkType, setLinkType] = useState<DestinationUrlInput>(parsedLinkComponents.linkType);
  const [linkValue, setLinkValue] = useState<string>(parsedLinkComponents.linkValue);
  const [showVariations, setShowVariations] = useState<boolean>(parsedLinkComponents.showVariations ?? false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const handleLinkTypeChange = useCallback((newType: DestinationUrlInput) => {
    setLinkType(newType);
    setLinkValue('');
    setShowVariations(false);
  }, []);

  const handleLinkValueChange = useCallback((newValue: string) => setLinkValue(newValue), []);

  const handleShowVariationsChange = useCallback((checked: boolean) => setShowVariations(checked), []);

  useEffect(() => {
    if (linkType && linkValue) {
      const validation = validateLinkValue(linkType, linkValue);
      setValidationError(validation.isValid ? undefined : validation.errorMessage);
    }
  }, [
    linkType,
    linkValue,
  ]);

  return {
    handleLinkTypeChange,
    handleLinkValueChange,
    handleShowVariationsChange,
    linkType,
    linkValue,
    showVariations,
    validationError,
  };
};
